/**
 * Switch — the labelled on/off toggle used across settings forms.
 *
 * Two exports:
 *   - `Switch` — bare pill+knob control (44×24). Use when the caller lays
 *     out its own label / description (e.g. inside a table cell).
 *   - `SwitchRow` — the card-style row wrapper (title + description + switch
 *     right-aligned). Used by vendor / approvals / notifications / bank-account
 *     forms; all four had inlined identical copies before this primitive.
 *
 * The knob carries a drop-shadow + ring so it stays visually distinct on
 * both the ON (bg-ok) and OFF (bg-line-2) tracks. The prior custom
 * implementations used `bg-paper` without a shadow, so the knob visually
 * disappeared against the green ON state.
 */
import * as React from "react";
import { cn } from "../lib/cn.js";

export interface SwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  id?: string;
  className?: string;
}

export const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { checked, onCheckedChange, disabled, className, ...aria },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onCheckedChange(!checked)}
      className={cn(
        "relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors",
        "focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2",
        "disabled:cursor-not-allowed disabled:opacity-50",
        checked ? "bg-ok" : "bg-line-2",
        className,
      )}
      {...aria}
    >
      <span
        aria-hidden
        className={cn(
          "pointer-events-none absolute top-1 h-4 w-4 rounded-full bg-white",
          "shadow-[0_1px_2px_rgba(0,0,0,0.25)] ring-1 ring-black/10",
          "transition-transform duration-150 ease-out",
          checked ? "translate-x-6" : "translate-x-1",
        )}
      />
    </button>
  );
});

export interface SwitchRowProps extends SwitchProps {
  title: string;
  description?: string;
}

export function SwitchRow({ title, description, ...switchProps }: SwitchRowProps) {
  const labelId = React.useId();
  return (
    <div className="mt-3 flex items-center justify-between gap-4 rounded-md border border-line bg-raised p-3">
      <div>
        <p id={labelId} className="text-sm font-semibold text-ink">
          {title}
        </p>
        {description ? <p className="mt-0.5 text-xs text-muted">{description}</p> : null}
      </div>
      <Switch {...switchProps} aria-labelledby={switchProps["aria-labelledby"] ?? labelId} />
    </div>
  );
}
